// UX-Vorschlag P4: Portfolio-Liste „Was braucht Aufmerksamkeit?". Reine Logik:
// Zeilen aus Projektliste + Kennzahlen (GET /api/portfolio) bauen,
// filtern und sortieren. Health kommt aus lib/projectHealth.ts — dieselbe
// Ampel wie auf den Karten.

import type { PortfolioProjectDto, ProjectListItem } from "@juuplan/shared";
import { computeProjectHealth, type ProjectHealth } from "./projectHealth";

export interface PortfolioRow {
  project: ProjectListItem;
  health: ProjectHealth;
  /** 0–100, gerundet. */
  progress: number;
  /** null = Kennzahlen (noch) nicht geladen bzw. Server kennt den Endpoint nicht. */
  overdue: number | null;
  blocked: number | null;
  nextMilestone: PortfolioProjectDto["next_milestone"];
  lastActivity: string | null;
}

export type PortfolioSortKey =
  | "name"
  | "health"
  | "overdue"
  | "blocked"
  | "milestone"
  | "progress"
  | "owner"
  | "activity";

export interface PortfolioSort {
  key: PortfolioSortKey;
  dir: "asc" | "desc";
}

const HEALTH_RANK: Record<ProjectHealth["level"], number> = {
  red: 0,
  yellow: 1,
  neutral: 2,
  green: 3,
};

export function buildPortfolioRows(
  projects: ProjectListItem[],
  metrics: PortfolioProjectDto[] | null,
): PortfolioRow[] {
  const byId = new Map((metrics ?? []).map((m) => [m.id, m] as const));
  return projects.map((p) => {
    const m = byId.get(p.id);
    return {
      project: p,
      health: computeProjectHealth(p),
      progress: p.tasks_total > 0 ? Math.round((p.tasks_done / p.tasks_total) * 100) : 0,
      overdue: m ? m.overdue_count : null,
      blocked: m ? m.blocked_count : null,
      nextMilestone: m?.next_milestone ?? null,
      lastActivity: m?.last_activity_at ?? null,
    };
  });
}

/**
 * „Braucht Aufmerksamkeit": ueberfaellig > 0, blockiert > 0 oder Health
 * nicht „auf Kurs" (gelb/rot). Neutral (z. B. ohne Termine) zaehlt nicht.
 */
export function needsAttention(r: PortfolioRow): boolean {
  return (
    (r.overdue ?? 0) > 0 ||
    (r.blocked ?? 0) > 0 ||
    r.health.level === "red" ||
    r.health.level === "yellow"
  );
}

function cmp(a: string | number | null, b: string | number | null): number {
  // Leere Werte immer ans Ende, unabhaengig von der Richtung (siehe sortPortfolio).
  if (a === null && b === null) return 0;
  if (a === null) return 1;
  if (b === null) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), "de");
}

export function sortPortfolio(
  rows: PortfolioRow[],
  sort: PortfolioSort,
  ownerName: (id: number) => string = (id) => String(id),
): PortfolioRow[] {
  const val = (r: PortfolioRow): string | number | null => {
    switch (sort.key) {
      case "name":
        return r.project.name;
      case "health":
        return HEALTH_RANK[r.health.level];
      case "overdue":
        return r.overdue;
      case "blocked":
        return r.blocked;
      case "milestone":
        return r.nextMilestone?.date ?? null;
      case "progress":
        return r.progress;
      case "owner":
        return ownerName(r.project.owner_id);
      case "activity":
        return r.lastActivity;
    }
  };
  const sign = sort.dir === "asc" ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = val(a);
    const vb = val(b);
    if (va === null || vb === null) return cmp(va, vb);
    const c = cmp(va, vb) * sign;
    return c !== 0 ? c : a.project.name.localeCompare(b.project.name, "de");
  });
}

/** Standard-Sortierung: dringendstes zuerst (Health, dann ueberfaellig). */
export const DEFAULT_PORTFOLIO_SORT: PortfolioSort = { key: "health", dir: "asc" };
